import type { Rule } from "@spoonity/flags-contract";
import { ConditionRow, defaultConditionForMatcher } from "./ConditionRow";
import { DistributionEditor } from "./DistributionEditor";

function nextRuleId(existing: Rule[]): string {
  let i = existing.length + 1;
  while (existing.some((r) => r.id === `rule-${i}`)) i += 1;
  return `rule-${i}`;
}

const RULE_ID_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9_-]{0,63}$/;

/** Ordered rules: add/remove/reorder, each with AND-ed conditions and a distribution (task brief). */
export function RulesBuilder({
  rules,
  treatmentNames,
  segmentNames,
  onChange,
  readOnly,
}: {
  rules: Rule[];
  treatmentNames: string[];
  segmentNames: string[];
  onChange: (next: Rule[]) => void;
  readOnly?: boolean;
}) {
  function updateRule(index: number, patch: Partial<Rule>) {
    onChange(rules.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  function removeRule(index: number) {
    onChange(rules.filter((_, i) => i !== index));
  }

  function move(index: number, dir: -1 | 1) {
    const target = index + dir;
    if (target < 0 || target >= rules.length) return;
    const next = rules.slice();
    const [item] = next.splice(index, 1);
    next.splice(target, 0, item!);
    onChange(next);
  }

  function addRule() {
    onChange([
      ...rules,
      {
        id: nextRuleId(rules),
        label: "",
        conditions: [defaultConditionForMatcher("equals")],
        distribution: treatmentNames.slice(0, 1).map((t) => ({ treatment: t, percent: 100 })),
      },
    ]);
  }

  return (
    <div>
      {rules.map((rule, i) => {
        const idError = RULE_ID_PATTERN.test(rule.id) ? null : "id must match ^[a-zA-Z0-9][a-zA-Z0-9_-]{0,63}$";
        const duplicate = rules.filter((r) => r.id === rule.id).length > 1;
        return (
          <div className="rule-card" key={i} data-testid={`rule-${i}`}>
            <div className="row wrap">
              <div className="field" style={{ marginBottom: 0, flex: "0 0 180px" }}>
                <label>Rule id</label>
                <input
                  value={rule.id}
                  disabled={readOnly}
                  aria-label="Rule id"
                  onChange={(e) => updateRule(i, { id: e.target.value })}
                />
                {idError && <span className="error">{idError}</span>}
                {duplicate && <span className="error">duplicate rule id</span>}
              </div>
              <div className="field" style={{ marginBottom: 0, flex: 1 }}>
                <label>Label</label>
                <input
                  value={rule.label ?? ""}
                  disabled={readOnly}
                  aria-label="Rule label"
                  onChange={(e) => updateRule(i, { label: e.target.value })}
                />
              </div>
              {!readOnly && (
                <div className="row" style={{ alignSelf: "flex-end" }}>
                  <button type="button" className="btn small" onClick={() => move(i, -1)} aria-label="Move up">
                    ↑
                  </button>
                  <button type="button" className="btn small" onClick={() => move(i, 1)} aria-label="Move down">
                    ↓
                  </button>
                  <button type="button" className="btn small danger" onClick={() => removeRule(i)}>
                    Remove rule
                  </button>
                </div>
              )}
            </div>

            <div className="hint" style={{ margin: "var(--space-2) 0" }}>
              All conditions must match (AND):
            </div>
            {rule.conditions.map((condition, ci) => (
              <ConditionRow
                key={ci}
                condition={condition}
                segmentNames={segmentNames}
                readOnly={readOnly}
                onChange={(next) =>
                  updateRule(i, {
                    conditions: rule.conditions.map((c, j) => (j === ci ? next : c)),
                  })
                }
                onRemove={() => updateRule(i, { conditions: rule.conditions.filter((_, j) => j !== ci) })}
              />
            ))}
            {!readOnly && (
              <button
                type="button"
                className="btn small"
                onClick={() =>
                  updateRule(i, { conditions: [...rule.conditions, defaultConditionForMatcher("equals")] })
                }
              >
                Add condition
              </button>
            )}

            <div style={{ marginTop: "var(--space-3)" }}>
              <DistributionEditor
                distribution={rule.distribution}
                treatmentNames={treatmentNames}
                readOnly={readOnly}
                onChange={(next) => updateRule(i, { distribution: next })}
              />
            </div>
          </div>
        );
      })}
      {!readOnly && (
        <button type="button" className="btn" onClick={addRule}>
          Add rule
        </button>
      )}
    </div>
  );
}
